import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseAutocomplete, BaseSelect, BaseUploadFile } from '~/components/common';
import { FormikErrors, getIn, useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, SAVE_TYPES } from '~/utils/constants/common';
import { cast, generatePersonName, handleApiError, notify } from '~/utils/helper';
import { CloseIcon, DeleteCircle, PlusIcon } from '~/assets/icons';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';
import useQuickbook from '~/hooks/useQuickbook';
import { handleGetQuickbookCustomers } from '~/thunks/quickbook/quickbookThunk';
import { jobActions } from '~/thunks/job/jobSlice';
import { CUSTOMER_TYPES_CONTACT, CUSTOMER_TYPES_ORGANIZATION } from '~/utils/constants/customers';
import { ICustomer, ICustomerField, IEditCustomer, ISelectCustomer } from '~/utils/interface/customer';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import {
  MASTER_DATA_ALL_CUSTOMERS,
  MASTER_DATA_CRM_CATEGORIES,
  MASTER_DATA_CUSTOMER_STATUSES,
  MASTER_DATA_CUSTOMER_TYPE,
  MASTER_DATA_CUSTOMERS,
  MASTER_DATA_REFERRAL_SOURCES,
} from '~/utils/constants/master';
import { UploadFile } from '~/utils/types/common';
import { handleCreateCustomer, handleEditCustomer } from '~/thunks/customer/customerThunk';
import { commonActions } from '~/thunks/common/commonSlice';
import { handleGetGoogleMapsDetail, handleGetGoogleSearch } from '~/thunks/google/googleThunk';
import { IGoogleSearch } from '~/utils/interface/google';
import classNames from 'classnames/bind';
import styles from './EditCustomerDialog.module.scss';

const cx = classNames.bind(styles);
type EditCustomerDialogProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  mode: string;
  data?: ICustomer;
  addExistMode?: boolean;
  jobId?: string;
  type?: string;
  customer?: ICustomer;
};

const initialValues: IEditCustomer = {
  type: CUSTOMER_TYPES_CONTACT,
  name: '',
  firstName: '',
  lastName: '',
  relationshipType: '',
  customerType: [],
  email: '',
  phone: '',
  parentId: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  statusId: '',
  salespersonId: '',
  referralSourceId: '',
  isAddressSameParent: false,
  quickbookCustomerDisplayName: '',
  crmCategoryId: '',
  jobTitle: '',
  isCrm: false,
  customerFields: [],
  photo: undefined,
  createCRMContact: false,
  crmContact: {},
};

const EditCustomerDialog = (props: EditCustomerDialogProps) => {
  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE, type = CUSTOMER_TYPES_CONTACT } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/customer', 'admin/common']);
  const dispatch = useAppDispatch();
  const { quickbook } = useQuickbook();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const submitValues = { ...values };
      if (mode === DIALOG_MODE_CREATE) {
        dispatch(commonActions.setIsLoading(true));
        try {
          await dispatch(handleCreateCustomer(submitValues)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        dispatch(commonActions.setIsLoading(true));
        try {
          await dispatch(
            handleEditCustomer({
              id: props.data?.id,
              params: submitValues,
            })
          ).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        } finally {
          dispatch(commonActions.setIsLoading(false));
        }
        return;
      }
    },
  });
  //#endregion Declare Hook
  //#region Selector
  const { customerStatuses, customers, allCustomers, referralSources, crmCategories, customerType } = useAppSelector(
    (state) => state.master
  );
  const { selectionAccounts } = useAppSelector((state) => state.account);
  const { quickbookCustomers } = useAppSelector((state) => state.quickbook);
  const { options } = useAppSelector((state) => state.google);
  //#endregion Selector

  //#region Declare State
  const [customerQuickbookOption, setCustomerQuickbookOption] = useState<string>(SAVE_TYPES.NO);
  const [allCustomerGroups, setAllCustomerGroups] = useState<Array<ISelectCustomer>>();

  const newOrExistingOption = useMemo(
    () => [
      {
        id: SAVE_TYPES.NEW,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NEW}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.EXIST,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.EXIST}`, { ns: 'admin/common' }),
      },
    ],
    [t]
  );

  const newOrExistingOptionalOption = useMemo(
    () => [
      {
        id: SAVE_TYPES.NO,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NO}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.NEW,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NEW}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.EXIST,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.EXIST}`, { ns: 'admin/common' }),
      },
    ],
    [t]
  );
  //#endregion Declare State

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    dispatch(
      handleGetMasterData({
        type: [
          MASTER_DATA_CUSTOMERS,
          MASTER_DATA_ALL_CUSTOMERS,
          MASTER_DATA_REFERRAL_SOURCES,
          MASTER_DATA_CUSTOMER_TYPE,
          MASTER_DATA_CUSTOMER_STATUSES,
          MASTER_DATA_CRM_CATEGORIES,
        ],
      })
    )
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    if (props.jobId) form.setFieldValue('jobId', props.jobId);
    if (type) form.setFieldValue('type', type);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (form.values?.customerOption === SAVE_TYPES.NEW) {
      form.setFieldValue('type', type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values?.customerOption]);

  useEffect(() => {
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('isCrm', true);
      quickbook?.isQuickbookConnected && setCustomerQuickbookOption(SAVE_TYPES.NEW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (allCustomers?.length) {
      const organizations: Array<ISelectCustomer> = [
        {
          id: CUSTOMER_TYPES_ORGANIZATION,
          name: t('properties.organization'),
          isGroup: true,
        },
      ];

      const contacts: Array<ISelectCustomer> = [
        {
          id: CUSTOMER_TYPES_CONTACT,
          name: t('properties.contact'),
          isGroup: true,
        },
      ];

      for (let customer of allCustomers) {
        if (customer.type === CUSTOMER_TYPES_ORGANIZATION) {
          organizations.push({
            id: customer?.id,
            name: customer?.name,
          });
        }
        if (customer.type === CUSTOMER_TYPES_CONTACT) {
          contacts.push({
            id: customer?.id,
            name: customer?.name,
          });
        }
      }
      setAllCustomerGroups(organizations.concat(contacts));
    }
  }, [allCustomers, t]);

  useEffect(() => {
    if (quickbook?.isQuickbookConnected) {
      if (customerQuickbookOption === SAVE_TYPES.NO) {
        form.setFieldValue('quickbookCustomerDisplayName', '');
        form.setFieldValue('quickbookCustomer', '');
      }

      if (customerQuickbookOption === SAVE_TYPES.NEW) {
        form.setFieldValue(
          'quickbookCustomerDisplayName',
          type === CUSTOMER_TYPES_CONTACT
            ? generatePersonName(form.values.firstName, form.values.lastName)
            : form.values.name
        );
      }

      if (customerQuickbookOption === SAVE_TYPES.EXIST) {
        dispatch(handleGetQuickbookCustomers())
          .unwrap()
          .catch((e) => {
            handleApiError(e);
          });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuickbookOption, quickbook?.isQuickbookConnected]);

  useEffect(() => {
    if (quickbook?.isQuickbookConnected && customerQuickbookOption === SAVE_TYPES.NEW) {
      form.setFieldValue(
        'quickbookCustomerDisplayName',
        type === CUSTOMER_TYPES_CONTACT
          ? generatePersonName(form.values.firstName, form.values.lastName)
          : form.values.name
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quickbook?.isQuickbookConnected,
    customerQuickbookOption,
    form.values.firstName,
    form.values.lastName,
    form.values.name,
  ]);

  useEffect(() => {
    if (props.data) {
      form.setFieldValue('customerOption', SAVE_TYPES.EXIST, false);
      form.setFieldValue('type', props.data?.type, false);
      form.setFieldValue('customerId', cast(props.data?.id));
      form.setFieldValue('name', cast(props.data?.name));
      form.setFieldValue('firstName', cast(props.data?.firstName));
      form.setFieldValue('lastName', cast(props.data?.lastName));
      form.setFieldValue('email', cast(props.data?.email));
      form.setFieldValue('address', cast(props.data?.address));
      form.setFieldValue('address2', cast(props.data?.address2));
      form.setFieldValue('city', cast(props.data?.city));
      form.setFieldValue('state', cast(props.data?.state));
      form.setFieldValue('zipCode', cast(props.data?.zipCode));
      form.setFieldValue('phone', cast(props.data?.phone));
      form.setFieldValue('country', cast(props.data?.country));
      form.setFieldValue('relationshipType', cast(props.data?.relationshipType));
      form.setFieldValue('customerType', cast(props.data?.customerType));
      form.setFieldValue('quickbookCustomer', cast(props.data?.quickbookCustomer));
      form.setFieldValue('statusId', cast(props.data?.statusId));
      form.setFieldValue('referralSourceId', cast(props.data?.referralSourceId));
      form.setFieldValue('salespersonId', cast(props.data?.salespersonId));
      form.setFieldValue('customerFields', cast(props.data?.customerFields));
      form.setFieldValue('isCrm', props.data?.isCrm);
      form.setFieldValue('parentId', cast(props.data?.parentId));
      form.setFieldValue('jobTitle', cast(props.data?.jobTitle));
      form.setFieldValue('crmCategoryId', cast(props.data?.crmCategoryId));
      form.setFieldValue('photo', cast(props.data?.photo));

      if (props?.data?.quickbookCustomer) {
        setCustomerQuickbookOption(SAVE_TYPES.EXIST);
      } else {
        setCustomerQuickbookOption(SAVE_TYPES.NO);
      }

      if (props.data?.crmContact) {
        form.setFieldValue('createCRMContact', true);
        form.setFieldValue('crmContact.firstName', cast(props.data?.crmContact?.firstName));
        form.setFieldValue('crmContact.lastName', cast(props.data?.crmContact?.lastName));
        form.setFieldValue('crmContact.phone', cast(props.data?.crmContact?.phone));
        form.setFieldValue('crmContact.email', cast(props.data?.crmContact?.email));
        form.setFieldValue('crmContact.jobTitle', cast(props.data?.crmContact?.jobTitle));
        form.setFieldValue('crmContact.crmCategoryId', cast(props.data?.crmContact?.crmCategoryId));
        form.setFieldValue('crmContact.address', cast(props.data?.crmContact?.address));
        form.setFieldValue('crmContact.address2', cast(props.data?.crmContact?.address2));
        form.setFieldValue('crmContact.city', cast(props.data?.crmContact?.city));
        form.setFieldValue('crmContact.state', cast(props.data?.crmContact?.state));
        form.setFieldValue('crmContact.zipCode', cast(props.data?.crmContact?.zipCode));
        form.setFieldValue('crmContact.country', cast(props.data?.crmContact?.country));
        form.setFieldValue('crmContact.photo', cast(props.data?.crmContact?.photo?.url));
      }

      if (props.data?.customerFields) {
        form.setFieldValue('customerFields', props.data?.customerFields);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  useEffect(() => {
    form.setFieldValue('parentId', cast(props.data?.parentId ?? props.customer?.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customer, props.open]);

  useEffect(() => {
    form.setFieldValue('type', type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    props.setOpen(open, needRefreshData);
  };

  const addNewCustomerField = () => {
    form.setFieldValue('customerFields', [
      ...(form.values.customerFields ?? []),
      {
        name: undefined,
        value: undefined,
      },
    ]);
  };

  const handleDelete = (index: number) => {
    const updatedFields = [...(form.values.customerFields ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('customerFields', updatedFields);
  };

  const renderCustomerField = (_item: ICustomerField, index: number) => {
    const customerFieldError =
      (form.errors.customerFields && (form.errors.customerFields[index] as FormikErrors<ICustomerField>)) || undefined;

    return (
      <Grid container spacing={3} className='mb-[24px]' key={index}>
        <Grid item xs={4} sm={4}>
          <TextField
            name={`customerFields.${index}.name`}
            placeholder={t('properties.fieldName')}
            value={form.values?.customerFields && form.values?.customerFields[index].name}
            onChange={form.handleChange}
            error={Boolean(customerFieldError?.name)}
            helperText={customerFieldError?.name}
            fullWidth
          />
        </Grid>
        <Grid item xs={4} sm={4}>
          <TextField
            name={`customerFields.${index}.value`}
            placeholder={t('properties.fieldValue')}
            value={form.values.customerFields && form.values.customerFields[index].value}
            onChange={form.handleChange}
            error={Boolean(customerFieldError?.value)}
            helperText={customerFieldError?.value}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={1} className='flex items-center justify-center'>
          <DeleteCircle className='w-[20px] h-[20px] cursor-pointer' onClick={() => handleDelete(index)} />
        </Grid>
      </Grid>
    );
  };

  const handleSearch = async (inputValue: string) => {
    dispatch(handleGetGoogleSearch({ query: inputValue }));
  };

  const handleSelect = async (selectedOption: IGoogleSearch) => {
    const res = await dispatch(handleGetGoogleMapsDetail({ query: selectedOption.placeId }));
    form.setFieldValue('address', cast(res?.payload.address), false);
    form.setFieldValue('city', cast(res?.payload.city), false);
    form.setFieldValue('state', cast(res?.payload.state), false);
    form.setFieldValue('zipCode', cast(res?.payload.zipCode), false);
    form.setFieldValue('country', cast(res?.payload.country), false);
  };

  const handleChangeText = (text: string) => {
    form.setFieldValue('address', cast(text), false);
  };

  //#endregion Handle Function
  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE
          ? t('addNew')
          : form.values.type === CUSTOMER_TYPES_CONTACT
          ? t('editContact')
          : t('editOrganization')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          {props.addExistMode && (
            <Grid container spacing={3} className='mb-[24px]'>
              <Grid item xs={12} sm={4}>
                <InputLabel required={true}>{t('option')}</InputLabel>
                <BaseSelect
                  name='customerOption'
                  value={cast(form.values?.customerOption)}
                  options={newOrExistingOption}
                  label={t('chooseOne')}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'customerOption'))}
                  helperText={getIn(form.errors, 'customerOption')}
                />
              </Grid>
              {form.values?.customerOption === SAVE_TYPES.EXIST && (
                <Grid item xs={12} sm={4}>
                  <InputLabel required={true}>{t('properties.contact')}</InputLabel>
                  <BaseSelect
                    name='customerId'
                    options={allCustomerGroups}
                    value={cast(form.values?.customerId)}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.customerId)}
                    helperText={form.errors?.customerId}
                    isSearchEnable={true}
                  />
                </Grid>
              )}

              {form.values?.customerOption === SAVE_TYPES.NEW && (
                <Grid item xs={12} sm={4}>
                  <InputLabel required={true}>{t('customerType')}</InputLabel>
                  <FormControl>
                    <RadioGroup row aria-labelledby='customer-type' name='type' defaultValue={CUSTOMER_TYPES_CONTACT}>
                      <FormControlLabel
                        value={CUSTOMER_TYPES_ORGANIZATION}
                        control={<Radio onChange={form.handleChange} />}
                        label={t('types.organization')}
                      />
                      <FormControlLabel
                        value={CUSTOMER_TYPES_CONTACT}
                        control={<Radio onChange={form.handleChange} />}
                        label={t('types.contact')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          )}

          {(props.addExistMode === undefined ||
            (props.addExistMode && form.values?.customerOption === SAVE_TYPES.NEW)) && (
            <>
              <Grid container spacing={3} className='mb-[24px]'>
                {form.values.type === CUSTOMER_TYPES_ORGANIZATION && (
                  <Grid item xs={12} sm={4}>
                    <InputLabel required={true}>{t('properties.name')}</InputLabel>
                    <TextField
                      name='name'
                      placeholder={t('properties.name')}
                      value={cast(form.values?.name)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'name'))}
                      helperText={getIn(form.errors, 'name')}
                    />
                  </Grid>
                )}

                {form.values.type === CUSTOMER_TYPES_CONTACT && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <InputLabel required={true}>{t('properties.firstName')}</InputLabel>
                      <TextField
                        name='firstName'
                        placeholder={t('properties.firstName')}
                        value={cast(form.values?.firstName)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'firstName'))}
                        helperText={getIn(form.errors, 'firstName')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputLabel>{t('properties.lastName')}</InputLabel>
                      <TextField
                        name='lastName'
                        placeholder={t('properties.lastName')}
                        value={cast(form.values?.lastName)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'lastName'))}
                        helperText={getIn(form.errors, 'lastName')}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.type')}</InputLabel>
                  {/* <TextField
                    name='relationshipType'
                    placeholder={t('properties.type')}
                    value={cast(form.values?.relationshipType)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'relationshipType'))}
                    helperText={getIn(form.errors, 'relationshipType')}
                  /> */}

                  <BaseSelect
                    name='customerType'
                    value={form.values.customerType}
                    options={customerType}
                    label={t('properties.type')}
                    onChange={(e) => {
                      form.setFieldValue('customerType', e.target.value);
                    }}
                    error={Boolean(form.errors?.customerType)}
                    multiple
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.email')}</InputLabel>
                  <TextField
                    name='email'
                    placeholder={t('properties.email')}
                    value={cast(form.values?.email)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'email'))}
                    helperText={getIn(form.errors, 'email')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.phone')}</InputLabel>
                  <TextField
                    name='phone'
                    placeholder={t('properties.phone')}
                    value={cast(form.values?.phone)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'phone'))}
                    helperText={getIn(form.errors, 'phone')}
                  />
                </Grid>
                {form.values.type === CUSTOMER_TYPES_CONTACT && (
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.customerName')}</InputLabel>
                    <BaseSelect
                      name='parentId'
                      options={customers}
                      label={t('properties.customerName')}
                      value={cast(form.values?.parentId)}
                      onChange={form.handleChange}
                      isSearchEnable={true}
                      error={Boolean(getIn(form.errors, 'parentId'))}
                      helperText={getIn(form.errors, 'parentId')}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.status')}</InputLabel>
                  <BaseSelect
                    name='statusId'
                    options={customerStatuses}
                    isHideIconCheckbox={true}
                    label={t('properties.status')}
                    value={cast(form.values?.statusId)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'statusId'))}
                    helperText={getIn(form.errors, 'statusId')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.salePerson')}</InputLabel>
                  <BaseSelect
                    name='salespersonId'
                    options={selectionAccounts}
                    isHideIconCheckbox={true}
                    label={t('properties.salePerson')}
                    value={cast(form.values?.salespersonId)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'salespersonId'))}
                    helperText={getIn(form.errors, 'salespersonId')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.referralResource')}</InputLabel>
                  <BaseSelect
                    name='referralSourceId'
                    options={referralSources}
                    isHideIconCheckbox={true}
                    label={t('properties.salePerson')}
                    value={cast(form.values?.referralSourceId)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'referralSourceId'))}
                    helperText={getIn(form.errors, 'referralSourceId')}
                  />
                </Grid>
                {form.values.type === CUSTOMER_TYPES_CONTACT && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <InputLabel>{t('properties.jobTitle')}</InputLabel>
                      <TextField
                        name='jobTitle'
                        placeholder={t('properties.jobTitle')}
                        value={form.values.jobTitle}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.jobTitle)}
                        helperText={form.errors.jobTitle}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <InputLabel>{t('properties.category')}</InputLabel>
                      <BaseSelect
                        name='crmCategoryId'
                        placeholder={t('properties.category')}
                        value={form.values.crmCategoryId}
                        options={crmCategories}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.crmCategoryId)}
                        helperText={form.errors.crmCategoryId}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <InputLabel className='mt-[20px]'></InputLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.values.isCrm}
                      onChange={(e) => form.setFieldValue('isCrm', e.target.checked)}
                    />
                  }
                  label={
                    form.values.type === CUSTOMER_TYPES_CONTACT
                      ? t('properties.addContactToCRM')
                      : t('properties.addOrganizationToCRM')
                  }
                  name='isCrm'
                  value={true}
                />
              </Grid>
              <Grid container spacing={3} className='mb-[24px] mt-[10px]'>
                <Grid item xs={12} sm={4}>
                  <InputLabel>{t('properties.linkToQuickbookCustomer')}</InputLabel>
                  <BaseSelect
                    value={customerQuickbookOption}
                    options={newOrExistingOptionalOption}
                    onChange={(e) => {
                      setCustomerQuickbookOption(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  {customerQuickbookOption === SAVE_TYPES.NEW && (
                    <>
                      <InputLabel required={true}>{t('properties.quickbookCustomerDisplayName')}</InputLabel>
                      {quickbook?.isQuickbookConnected ? (
                        <TextField
                          name='quickbookCustomerDisplayName'
                          value={cast(form.values?.quickbookCustomerDisplayName)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'quickbookCustomerDisplayName'))}
                          helperText={getIn(form.errors, 'quickbookCustomerDisplayName')}
                        />
                      ) : (
                        <Typography color='error'>{t('pleaseConnectToQuickbooksFirst')}</Typography>
                      )}
                    </>
                  )}

                  {customerQuickbookOption === SAVE_TYPES.EXIST && (
                    <>
                      <InputLabel>{t('properties.quickbookCustomer')}</InputLabel>
                      {quickbook?.isQuickbookConnected ? (
                        <BaseSelect
                          name='quickbookCustomer'
                          value={cast(form.values?.quickbookCustomer)}
                          options={quickbookCustomers}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'quickbookCustomer'))}
                          helperText={getIn(form.errors, 'quickbookCustomer')}
                        />
                      ) : (
                        <Typography color='error'>{t('pleaseConnectToQuickbooksFirst')}</Typography>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
              {form.values.type === CUSTOMER_TYPES_CONTACT && (
                <Grid container spacing={3} className='mb-[24px]'>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.values?.isAddressSameParent}
                          onChange={(e) => form.setFieldValue('isAddressSameParent', e.target.checked)}
                        />
                      }
                      label={t('sameasOrganizationAddress')}
                      name='isAddressSameParent'
                      value={true}
                    />
                  </Grid>
                </Grid>
              )}
              {!form.values?.isAddressSameParent && (
                <Grid container spacing={3} className='mb-[24px]'>
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.address')}</InputLabel>

                    <Box className={cx('box-input')}>
                      <BaseAutocomplete
                        value={
                          getIn(form.values, 'address')
                            ? {
                                id: '1',
                                name: getIn(form.values, 'address'),
                                group: 'Address',
                              }
                            : null
                        }
                        onSearch={handleSearch}
                        onSelected={handleSelect}
                        onChangeText={handleChangeText}
                        options={options}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.address2')}</InputLabel>
                    <TextField
                      name='address2'
                      placeholder={t('properties.address2')}
                      value={cast(form.values?.address2)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'address2'))}
                      helperText={getIn(form.errors, 'address2')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.city')}</InputLabel>
                    <TextField
                      name='city'
                      placeholder={t('properties.city')}
                      value={cast(form.values?.city)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'city'))}
                      helperText={getIn(form.errors, 'city')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.state')}</InputLabel>
                    <TextField
                      name='state'
                      placeholder={t('properties.state')}
                      value={cast(form.values?.state)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'state'))}
                      helperText={getIn(form.errors, 'state')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.zipCode')}</InputLabel>
                    <TextField
                      name='zipCode'
                      placeholder={t('properties.zipCode')}
                      value={cast(form.values?.zipCode)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'zipCode'))}
                      helperText={getIn(form.errors, 'zipCode')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel>{t('properties.country')}</InputLabel>
                    <TextField
                      name='country'
                      placeholder={t('properties.country')}
                      value={cast(form.values?.country)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'country'))}
                      helperText={getIn(form.errors, 'country')}
                    />
                  </Grid>
                </Grid>
              )}
              <Box>
                <InputLabel>{t('customFields')}</InputLabel>
              </Box>
              {form.values.customerFields?.map((item: ICustomerField, index: number) =>
                renderCustomerField(item, index)
              )}
              <Grid container spacing={3} className='mb-[24px]'>
                <Grid item xs={12} sm={4}>
                  <Button
                    onClick={addNewCustomerField}
                    className='whitespace-nowrap'
                    startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                  >
                    {t('addNewCustomerField')}
                  </Button>
                </Grid>
              </Grid>
              <Grid container spacing={3} className='mb-[30px]'>
                <Grid item xs={12}>
                  <InputLabel>{t('properties.uploadPhoto')}</InputLabel>
                  <BaseUploadFile
                    uploadedFiles={typeof form.values.photo === 'string' ? [{ url: form.values?.photo }] : undefined}
                    onChange={(files: Array<UploadFile>) => {
                      form.setFieldValue('photo', files[0]?.file ?? '');
                    }}
                  />
                </Grid>
              </Grid>

              {form.values.type === CUSTOMER_TYPES_ORGANIZATION && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.values.createCRMContact}
                          onChange={(e) => form.setFieldValue('createCRMContact', e.target.checked)}
                        />
                      }
                      label={t('properties.crmContact')}
                      name='createCRMContact'
                      value={true}
                    />
                  </Grid>
                  {form.values.createCRMContact && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.firstName')}</InputLabel>
                        <TextField
                          name='crmContact.firstName'
                          placeholder={t('properties.firstName')}
                          value={cast(form.values.crmContact?.firstName)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.firstName'))}
                          helperText={getIn(form.errors, 'crmContact.firstName')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.lastName')}</InputLabel>
                        <TextField
                          name='crmContact.lastName'
                          placeholder={t('properties.lastName')}
                          value={cast(form.values.crmContact?.lastName)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.lastName'))}
                          helperText={getIn(form.errors, 'crmContact.lastName')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.phone')}</InputLabel>
                        <TextField
                          name='crmContact.phone'
                          placeholder={t('properties.phone')}
                          value={cast(form.values.crmContact?.phone)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.phone'))}
                          helperText={getIn(form.errors, 'crmContact.phone')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.email')}</InputLabel>
                        <TextField
                          name='crmContact.email'
                          placeholder={t('properties.email')}
                          value={cast(form.values.crmContact?.email)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.email'))}
                          helperText={getIn(form.errors, 'crmContact.email')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.category')}</InputLabel>
                        <BaseSelect
                          name='crmContact.crmCategoryId'
                          placeholder={t('properties.category')}
                          value={cast(form.values.crmContact?.crmCategoryId)}
                          options={crmCategories}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.crmCategoryId'))}
                          helperText={getIn(form.errors, 'crmContact.crmCategoryId')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.jobTitle')}</InputLabel>
                        <TextField
                          name='crmContact.jobTitle'
                          placeholder={t('properties.jobTitle')}
                          value={cast(form.values.crmContact?.jobTitle)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.jobTitle'))}
                          helperText={getIn(form.errors, 'crmContact.jobTitle')}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.address')}</InputLabel>
                        <TextField
                          name='crmContact.address'
                          placeholder={t('properties.address')}
                          value={cast(cast(form.values.crmContact?.address))}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.address'))}
                          helperText={getIn(form.errors, 'crmContact.address')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.address2')}</InputLabel>
                        <TextField
                          name='crmContact.address2'
                          placeholder={t('properties.address2')}
                          value={cast(cast(form.values.crmContact?.address2))}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.address2'))}
                          helperText={getIn(form.errors, 'crmContact.address2')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.city')}</InputLabel>
                        <TextField
                          name='crmContact.city'
                          placeholder={t('properties.city')}
                          value={cast(form.values.crmContact?.city)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.city'))}
                          helperText={getIn(form.errors, 'crmContact.city')}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.state')}</InputLabel>
                        <TextField
                          name='crmContact.state'
                          placeholder={t('properties.state')}
                          value={cast(form.values.crmContact?.state)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.city'))}
                          helperText={getIn(form.errors, 'crmContact.city')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.zipCode')}</InputLabel>
                        <TextField
                          name='crmContact.zipCode'
                          placeholder={t('properties.zipCode')}
                          value={cast(form.values.crmContact?.zipCode)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.zipCode'))}
                          helperText={getIn(form.errors, 'crmContact.zipCode')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InputLabel>{t('properties.country')}</InputLabel>
                        <TextField
                          name='crmContact.country'
                          placeholder={t('properties.country')}
                          value={cast(form.values.crmContact?.country)}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, 'crmContact.country'))}
                          helperText={getIn(form.errors, 'contact.country')}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel>{t('properties.uploadPhoto')}</InputLabel>
                        <BaseUploadFile
                          uploadedFiles={
                            typeof form.values.crmContact?.photo === 'string'
                              ? [{ url: form.values.crmContact?.photo }]
                              : undefined
                          }
                          onChange={(files: Array<UploadFile>) => {
                            form.setFieldValue('crmContact.photo', files[0]?.file ?? '');
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditCustomerDialog);
